import { MaskedLines, Reveal, FadeIn } from "@/components/Motion";
import { STONES } from "@/data/stones";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const STATS = [
  { value: "25+", label: "Years in stone" },
  { value: "07", label: "Stone families" },
  { value: "400+", label: "Projects served" },
];

export default function About() {
  useSeo({
    title: "About Varun Marbles | 25+ Years Curating Fine Stone in Gurgaon",
    description:
      "A family yard grown into one of Gurgaon's most trusted curators of quartz, marble, granite and onyx. Architects, builders and homeowners choose us for stone without compromise.",
    path: "/about",
  });
  return (
    <>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-16 md:pb-28">
        <FadeIn delay={0.1}>
          <p data-testid="about-overline" className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-8">
            About Varun Marbles
          </p>
        </FadeIn>
        <h1 data-testid="about-headline" className="font-serif font-light tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          <MaskedLines
            delay={0.2}
            lines={[<>A house built on</>, <><span className="italic">beautiful</span> stone.</>]}
          />
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7 space-y-8">
            <Reveal>
              <p className="text-lg md:text-xl font-light leading-relaxed text-foreground/85" data-testid="about-lead">
                Varun Marbles began with a single belief — that stone is not a commodity, but the
                memory of the earth, cut to size. What started as a family yard has grown into one
                of the region's most trusted curators of quartz, marble, granite and onyx.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base font-light leading-relaxed text-muted-foreground">
                Today we specialise in quartz slabs — the modern workhorse of great kitchens and
                calm interiors — while continuing to source Italian and Indian marble, granite,
                and rare imported and natural onyx for those who want their spaces to say
                something lasting. Architects, builders and homeowners come to us for one reason:
                we say no to mediocre stone, so they never have to.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="grid grid-cols-3 gap-8 pt-10 border-t border-border" data-testid="about-stats">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <p className="font-serif text-4xl md:text-5xl font-light tracking-tight">{s.value}</p>
                    <p className="mt-2 text-xs tracking-[0.2em] uppercase text-secondary-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={STONES[1].image}
                  alt="Italian marble detail"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </div>
              <p className="mt-4 text-xs tracking-[0.25em] uppercase text-secondary-foreground">
                Calacatta-grade Italian marble
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 flex flex-wrap items-end justify-between gap-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] max-w-xl">
              Come, walk the <span className="italic">yard.</span>
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-muted-foreground">
              Stone chooses you as much as you choose it. Visit our gallery, or send an enquiry
              and we'll shortlist slabs for your project.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground/80"
            >
              Get in touch
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
